// src/components/Cockpit/RuntimeMatrix.tsx
// ============================================
// RuntimeMatrix - Runtime 状态网格（v2 视觉升级版）
//
// 视觉：
//   - 7 列网格，每个 tile 含图标 + 名称 + 状态点 + 进度条
//   - 状态色边框 + 心跳呼吸动画
//   - 悬浮显示详细信息（版本/调用次数/平均延迟/运行时长）
//   - 底部进度条显示健康度
// ============================================

import { motion } from "framer-motion";
import {
  MessageSquare,
  TrendingUp,
  BookOpen,
  Briefcase,
  FileText,
  GitBranch,
  Bot,
  Brain,
  Building2,
  Plug,
  Bell,
  Clock,
  FileCode,
  Wrench,
  Sparkles,
  Puzzle,
  Layers,
  Calendar,
  Newspaper,
  Compass,
  Share2,
  Rocket,
  Activity,
  Video,
  Network,
  Crown,
  Target,
  Users,
  Package,
  ClipboardList,
  Factory,
  CalendarClock,
  Lightbulb,
  Palette,
  RefreshCw,
  Megaphone,
  FolderKanban,
  PieChart,
  Gauge,
  BrainCircuit,
  Sprout,
  Zap,
  Hotel,
  Coins,
  Star,
  Radio,
  Wallet,
  Clapperboard,
  GitCommit,
  Route,
  Workflow,
  Play,
  type LucideIcon,
} from "lucide-react";
import { RuntimeName } from "../../types/runtime";
import type {
  RuntimeHealth,
  RuntimeStatus as RuntimeStatusType,
} from "../../types/runtime";
import type { RuntimeTile } from "../../hooks/useCockpitData";

// ============================================
// Runtime 元数据（图标 + 中文名 + 简称）
// ============================================
const RUNTIME_META: Partial<Record<
  RuntimeName,
  { icon: LucideIcon; label: string }
>> = {
  [RuntimeName.Hermes]: { icon: MessageSquare, label: "Hermes" },
  [RuntimeName.Quant]: { icon: TrendingUp, label: "Quant" },
  [RuntimeName.Knowledge]: { icon: BookOpen, label: "Knowledge" },
  [RuntimeName.Business]: { icon: Briefcase, label: "Business" },
  [RuntimeName.Content]: { icon: FileText, label: "Content" },
  [RuntimeName.Workflow]: { icon: GitBranch, label: "Workflow" },
  [RuntimeName.Agent]: { icon: Bot, label: "Agent" },
  [RuntimeName.Memory]: { icon: Brain, label: "Memory" },
  [RuntimeName.Enterprise]: { icon: Building2, label: "Enterprise" },
  [RuntimeName.Integration]: { icon: Plug, label: "Integration" },
  [RuntimeName.Notification]: { icon: Bell, label: "Notify" },
  [RuntimeName.Scheduler]: { icon: Clock, label: "Scheduler" },
  [RuntimeName.Prompt]: { icon: FileCode, label: "Prompt" },
  [RuntimeName.Tool]: { icon: Wrench, label: "Tool" },
  // 策略级：GEO / DSO / GrowthFactory / GEO 诊断
  [RuntimeName.GEO]: { icon: Compass, label: "GEO" },
  [RuntimeName.DSO]: { icon: Share2, label: "DSO" },
  [RuntimeName.GrowthFactory]: { icon: Rocket, label: "增长工厂" },
  [RuntimeName.GEODiagnostic]: { icon: Activity, label: "GEO诊断" },
  // P2-2 / P2-5 / P2-3 新增
  [RuntimeName.LLM]: { icon: Sparkles, label: "LLM" },
  [RuntimeName.Skill]: { icon: Puzzle, label: "Skill" },
  [RuntimeName.Workspace]: { icon: Layers, label: "Workspace" },
  // Sprint 1 内容生产流水线
  [RuntimeName.Calendar]: { icon: Calendar, label: "Calendar" },
  [RuntimeName.Editorial]: { icon: Newspaper, label: "Editorial" },
  [RuntimeName.VideoPipeline]: { icon: Video, label: "视频流水线" },
  // Sprint 2 知识图谱 / CEO / Sales
  [RuntimeName.KnowledgeGraph]: { icon: Network, label: "知识图谱" },
  [RuntimeName.CEO]: { icon: Crown, label: "CEO" },
  [RuntimeName.Sales]: { icon: Target, label: "Sales" },
  // Sprint 4 企业知识中台 / 内容工厂
  [RuntimeName.CRM]: { icon: Users, label: "CRM" },
  [RuntimeName.SKU]: { icon: Package, label: "SKU" },
  [RuntimeName.SOP]: { icon: ClipboardList, label: "SOP" },
  [RuntimeName.ContentFactory]: { icon: Factory, label: "内容工厂" },
  [RuntimeName.ContentCalendar]: { icon: CalendarClock, label: "内容日历" },
  [RuntimeName.TopicEngine]: { icon: Lightbulb, label: "选题引擎" },
  [RuntimeName.BrandLibrary]: { icon: Palette, label: "品牌库" },
  [RuntimeName.ContentLifecycle]: { icon: RefreshCw, label: "内容生命周期" },
  // Sprint 3 新闻中心 / Project
  [RuntimeName.News]: { icon: Megaphone, label: "News" },
  [RuntimeName.Project]: { icon: FolderKanban, label: "Project" },
  // 企业决策 / 运营 / 增长 / 自动驾驶
  [RuntimeName.BusinessEngine]: { icon: PieChart, label: "业务引擎" },
  [RuntimeName.BusinessOperator]: { icon: Gauge, label: "运营官" },
  [RuntimeName.EnterpriseBrain]: { icon: BrainCircuit, label: "企业大脑" },
  [RuntimeName.GrowthEngine]: { icon: Sprout, label: "增长引擎" },
  [RuntimeName.Autopilot]: { icon: Zap, label: "自动驾驶" },
  // Program A 产品 Runtime
  [RuntimeName.Hotel]: { icon: Hotel, label: "Hotel" },
  [RuntimeName.GoldFactory]: { icon: Coins, label: "黄金工厂" },
  [RuntimeName.AIDestiny]: { icon: Star, label: "AI Destiny" },
  [RuntimeName.MarketingOS]: { icon: Radio, label: "营销OS" },
  // Program C 收入引擎 / Phase D 媒体工作室
  [RuntimeName.RevenueEngine]: { icon: Wallet, label: "收入引擎" },
  [RuntimeName.MediaStudio]: { icon: Clapperboard, label: "媒体工作室" },
  // 知识演化 / 内容策略 / Coze 爆款分析
  [RuntimeName.KnowledgeEvolution]: { icon: GitCommit, label: "知识演化" },
  [RuntimeName.ContentStrategy]: { icon: Route, label: "内容策略" },
  [RuntimeName.CozeWorkflowGen]: { icon: Workflow, label: "Coze编排" },
  [RuntimeName.CozeExecutor]: { icon: Play, label: "Coze执行" },
};

// ============================================
// 状态 → 颜色映射
// ============================================
const STATUS_COLOR: Record<RuntimeStatusType, string> = {
  ready: "#34D399",
  loading: "#FBBF24",
  error: "#F87171",
  idle: "#6B7280",
  stopped: "#4B5563",
};

const HEALTH_DOT: Record<RuntimeHealth, string> = {
  healthy: "#34D399",
  degraded: "#FBBF24",
  unhealthy: "#F87171",
  unknown: "#6B7280",
};

/** 健康度 → 0-100 数值（用于进度条） */
const HEALTH_SCORE: Record<RuntimeHealth, number> = {
  healthy: 100,
  degraded: 60,
  unhealthy: 15,
  unknown: 30,
};

// ============================================
// 单个 Runtime tile
// ============================================
interface RuntimeTileProps {
  tile: RuntimeTile;
  index: number;
  onClick?: (name: RuntimeName) => void;
}

function RuntimeTileCard({ tile, index, onClick }: RuntimeTileProps) {
  // 安全取值：未命中 RUNTIME_META 时优雅降级（默认图标 Puzzle + 展示 tile.name），避免崩溃
  const meta = RUNTIME_META[tile.name];
  const Icon = meta?.icon ?? Puzzle;
  const label = meta?.label ?? tile.name;
  const color = STATUS_COLOR[tile.status];
  const isReady = tile.status === "ready";
  const healthScore = HEALTH_SCORE[tile.health];

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.85 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3, delay: 0.15 + index * 0.025, ease: "easeOut" }}
      whileHover={{ scale: 1.06, y: -2 }}
      onClick={() => onClick?.(tile.name)}
      className="group relative flex flex-col items-center justify-center rounded-lg border bg-white/[0.02] backdrop-blur-sm cursor-pointer transition-all px-1 py-2"
      style={{
        borderColor: `${color}30`,
        boxShadow: `0 0 0 1px ${color}10, 0 0 20px -10px ${color}`,
      }}
      title={`${label} · ${tile.status} · calls=${tile.calls}`}
    >
      {/* 心跳点 */}
      <span
        className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full"
        style={{ background: HEALTH_DOT[tile.health] }}
      >
        {isReady && (
          <motion.span
            className="absolute inset-0 rounded-full"
            style={{ background: color }}
            initial={{ opacity: 0.6, scale: 1 }}
            animate={{ opacity: 0, scale: 2.5 }}
            transition={{ duration: 1.6, repeat: Infinity, ease: "easeOut" }}
          />
        )}
      </span>

      <Icon size={16} style={{ color }} />
      <span className="mt-1 text-[9px] font-mono text-gray-300 group-hover:text-white transition-colors">
        {label}
      </span>

      {/* 底部进度条 */}
      <div className="mt-1 w-full h-0.5 bg-white/5 rounded-full overflow-hidden">
        <motion.div
          className="h-full rounded-full"
          style={{ background: color }}
          initial={{ width: 0 }}
          animate={{ width: `${healthScore}%` }}
          transition={{ duration: 0.6, delay: 0.2 + index * 0.025 }}
        />
      </div>

      {/* 悬浮提示 */}
      <div className="absolute z-20 bottom-full left-1/2 -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
        <div className="bg-[#0B1220] border border-white/10 rounded-md px-2 py-1.5 shadow-xl whitespace-nowrap">
          <div className="text-[11px] font-mono text-white">{label}</div>
          <div className="text-[10px] font-mono text-gray-400">
            v{tile.version} · {tile.calls} calls
          </div>
          <div className="text-[10px] font-mono text-gray-500">
            avg {tile.avgLatency}ms · up {tile.uptime}s
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// ============================================
// RuntimeMatrix 容器
// ============================================
interface RuntimeMatrixProps {
  runtimes: RuntimeTile[];
  onRuntimeClick?: (name: RuntimeName) => void;
}

export default function RuntimeMatrix({ runtimes, onRuntimeClick }: RuntimeMatrixProps) {
  const readyCount = runtimes.filter((r) => r.status === "ready").length;
  const totalCount = runtimes.length;
  const healthyPct = totalCount === 0 ? 0 : (readyCount / totalCount) * 100;

  return (
    <div className="relative rounded-xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-md p-4 h-full flex flex-col overflow-hidden">
      {/* 顶部光带 */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-500/40 to-transparent" />

      {/* 头部 */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-1 h-4 bg-gradient-to-b from-blue-400 to-blue-600 rounded-full" />
          <h3 className="text-sm font-medium text-white">Runtime Matrix</h3>
          <span className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">
            {totalCount} Modules
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-16 h-1 bg-white/5 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-emerald-400 to-emerald-500"
              initial={{ width: 0 }}
              animate={{ width: `${healthyPct}%` }}
              transition={{ duration: 0.6, ease: "easeOut" }}
            />
          </div>
          <span className="text-[11px] font-mono text-gray-400">
            <span className="text-emerald-400 font-bold">{readyCount}</span>
            <span className="text-gray-600"> / </span>
            <span>{totalCount}</span>
          </span>
        </div>
      </div>

      {/* 7 列网格 */}
      <div className="grid grid-cols-7 gap-1.5 flex-1 content-start">
        {runtimes.map((tile, idx) => (
          <RuntimeTileCard
            key={tile.name}
            tile={tile}
            index={idx}
            onClick={onRuntimeClick}
          />
        ))}
      </div>

      {/* 底部图例 */}
      <div className="mt-3 flex items-center gap-3 text-[10px] font-mono text-gray-500">
        <span className="flex items-center gap-1">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> Ready
        </span>
        <span className="flex items-center gap-1">
          <span className="w-1.5 h-1.5 rounded-full bg-yellow-400" /> Loading
        </span>
        <span className="flex items-center gap-1">
          <span className="w-1.5 h-1.5 rounded-full bg-red-400" /> Error
        </span>
        <span className="flex items-center gap-1">
          <span className="w-1.5 h-1.5 rounded-full bg-gray-500" /> Idle
        </span>
      </div>
    </div>
  );
}
